import { mapGetters } from 'vuex';
import { addDays, format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

export default {
  computed: {
    ...mapGetters('certifications', [
      'certifications',
      'certificationsList',
    ]),
    ...mapGetters('userCertifications', [
      'userCertifications',
      'userCertificationsList',
    ]),
    aseSubscriptionActionText() {
      return this.userNeedsToRenew ? 'Resubscribe' : 'Subscribe';
    },
    currentCertification() {
      return this.certifications[this.currentUserCertification.certificationId];
    },
    currentUserCertification() {
      if (this.currentResource.destination === 'user_certification') {
        return this.userCertifications[this.currentResource.resourceId];
      }
    },
    formattedSubEndDate() {
      if (this.subscriptionEndDate) {
        return format(this.subscriptionEndDate, 'MMMM d, yyyy');
      }
    },
    subscriptionEndDate() {
      const subscriptionReceipt = this.$store.getters['session/currentUser'].subscriptionReceipt;
      const subscriptionPoIds = this.$store.getters['purchaseOrders/subscription'].map(po => po.id);
      let prs = this.$store.getters['purchaseReceipts/all'];
      // prs only that are from subscription POs
      prs = prs.filter(pr => subscriptionPoIds.includes(pr.purchaseOrderId));
      // if user has an active subscription make sure it doesn't expire soon without a future renewal receipt in place
      if (subscriptionReceipt && subscriptionReceipt.expiresAt) {
        const expiration = new Date(subscriptionReceipt.expiresAt);
        // comparing epoch timestamps, if active sub receipt expires in 15 days
        if (expiration.setDate(expiration.getDate() - 15) < Date.now()) {
          // prs only that match existing sub with an expiration date. And we only care about subscriptions that have yet to expire.
          prs = prs.filter(pr => pr.purchaseOrderId === subscriptionReceipt.purchaseOrderId && pr.expiresAt && pr.expiresAt > Date.now());
          // see if there is a PR that starts in the future, if so, return false
          prs.sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt));
          if (prs.length > 0) {
            return prs[0].expiresAt && new Date(prs[0].expiresAt);
          } else {
            return new Date(subscriptionReceipt.expiresAt);
          }
          return prs[0].expiresAt && new Date(prs[0].expiresAt);
        } else {
          return new Date(subscriptionReceipt.expiresAt);
        }
      }
    },
    userCanPurchase() {
      // true if user has at least 1 non trial cert and has not already subscribed
      const hasOneCert = this.$store.getters['userCertifications/userCertificationsList'].find(uc => !this.$store.getters['certifications/certifications'][uc.certificationId].isTrial);
      const notSubscribed = !this.$store.getters['session/isSubscribed'];

      // But if the user has a subscription that starts in the future (this is possible with ASE), then they cannot purchase.
      let prs = [];
      const poIds = this.$store.getters['purchaseOrders/subscription'];
      if (Array.isArray(poIds) && poIds.length > 0) {
        const subscriptionPoIds = poIds.map(po => po.id);
        prs = this.$store.getters['purchaseReceipts/all'];
        prs = prs.filter(pr => subscriptionPoIds.includes(pr.purchaseOrderId));
      }
      const futureReceipts = prs.filter(pr => Date.now() < new Date(pr.startsAt).getTime());
      return hasOneCert && futureReceipts.length == 0 && (notSubscribed || this.userNeedsToRenew);
    },
    userNeedsToRenew() {
      const subscriptionReceipt = this.$store.getters['session/currentUser'].subscriptionReceipt;
      let prs = [];
      const poIds = this.$store.getters['purchaseOrders/subscription'];
      if (Array.isArray(poIds) && poIds.length > 0) {
        const subscriptionPoIds = poIds.map(po => po.id);
        prs = this.$store.getters['purchaseReceipts/all'];
        prs = prs.filter(pr => subscriptionPoIds.includes(pr.purchaseOrderId));
      }

      // if user has an active subscription make sure it doesn't expire soon without a future renewal receipt in place
      if (subscriptionReceipt && subscriptionReceipt.expiresAt) {
        const expiration = new Date(subscriptionReceipt.expiresAt);
        // comparing epoch timestamps, if active sub receipt expires in 15 days
        if (expiration.setDate(expiration.getDate() - 15) < Date.now()) {
          // prs only that match existing sub
          prs = prs.filter(pr => pr.purchaseOrderId === subscriptionReceipt.purchaseOrderId);
          // see if there is a PR that starts in the future, if so, return false
          return !prs.find(pr => new Date(pr.expiresAt).getTime() > Date.now() && Date.now() < new Date(pr.startsAt).getTime());
        }
      // otherwise just see if user has a former expired pr
      } else if (prs.find(pr => Date.now() > new Date(pr.expiresAt).getTime() && Date.now() > new Date(pr.startsAt).getTime())) {
        return true;
      }
    },
  },
  methods: {
    availableQuestions(uc, type = null) {
      let answeredQuestionIds = uc.questionsAnswered.map(aq => aq['id']);
      let delivered = uc.questionsDelivered;
      if (type) {
        delivered = delivered.filter(dq => dq['questionType'] === type);
      }
      return delivered.filter(dq => !answeredQuestionIds.includes(dq['id']));
    },
    expirationDate(uc) {
      return format(new Date(uc.expiresAt), 'MMMM d, yyyy');
    },
    nextQuestionDate(uc) {
      let event = uc.schedule.slice().sort((a, b) => new Date(a.deliveryDate) - new Date(b.deliveryDate))[0];
      if (event) {
        return format(this.utcDate(event.deliveryDate), 'EEEE, MMM. d');
      }
      else {
        return 'Subscription expiring soon';
      }
    },
    renewalDate(uc) {
      return format(new Date(uc.cycleEndsAt), 'MMMM d, yyyy');
    },
    subscriptionEndsBeforeNextEvent(uc) {
      // Get the date for the next question
      // this.subscriptionEndDate accounts for future dated PRs as well
      // Return true if nextDate is greater than final cert expiration (this.subscriptionEndDate)
      const next = uc?.schedule.slice().sort((a, b) => new Date(a.deliveryDate) - new Date(b.deliveryDate))[0];
      const nextDate = this.utcDate(next?.deliveryDate);
      if (nextDate) {
        return (this.subscriptionEndDate < nextDate);
      }
    },
    beyondSubscriptionEndDate(date) {
      return date > this.subscriptionEndDate;
    },
    userCertificationCompleted(uc) {
      let certification = this.certifications[uc.certificationId];
      return uc.creditsEarned >= certification.minimumCreditsRequired;
    },
    utcDate(dateString) {
      try {
        return utcToZonedTime(dateString, 'UTC');
      } catch (e) {
        // handle IE11 not supporting timezones
        if (e.name === 'RangeError') {
          // need to fudge the date by 1 here since it is utc
          return addDays(new Date(dateString), 1);
        } else {
          throw e;
        }
      }
    },
  },
};
